/**
 * Parses a ticket/event price from API (string or number, optional thousands separators).
 * @param {string|number|null|undefined} price
 * @returns {number|null}
 */
export function parseTicketPrice(price) {
  if (price === null || price === undefined || price === '') {
    return null;
  }
  const n = parseFloat(String(price).replace(/,/g, ''), 10);
  return Number.isFinite(n) ? n : null;
}

/**
 * Total monetary difference for the booking (per-ticket delta × quantity).
 * Positive means customer must pay more (top-up); negative means partial refund.
 * Values are in major currency units; rounded to 2 decimal places.
 *
 * @param {string|number} sourceUnitPrice
 * @param {string|number} targetUnitPrice
 * @param {number} quantity
 * @returns {number|null}
 */
export function transferPriceDelta(sourceUnitPrice, targetUnitPrice, quantity) {
  const s = parseTicketPrice(sourceUnitPrice);
  const t = parseTicketPrice(targetUnitPrice);
  if (s === null || t === null) {
    return null;
  }
  const q = Math.max(1, parseInt(String(quantity), 10) || 1);
  const raw = (t - s) * q;
  return Math.round(raw * 100) / 100;
}

/**
 * @param {string} symbol
 * @param {number} amountMajor
 * @returns {string}
 */
export function formatMoneyMajor(symbol, amountMajor) {
  const sym = symbol || '';
  const abs = Math.abs(amountMajor);
  const fixed = abs.toFixed(2);
  return sym + fixed;
}

/**
 * Settlement preview copy before the transfer request is submitted.
 *
 * @param {object} opts
 * @param {string|number} opts.sourceUnitPrice
 * @param {string|number} opts.targetUnitPrice
 * @param {number} [opts.quantity]
 * @param {string} [opts.currencySymbol]
 * @returns {{ kind: 'none'|'top_up'|'refund', headline: string, amountLine: string|null }}
 */
export function getSettlementPreview(opts) {
  const quantity = opts.quantity != null ? opts.quantity : 1;
  const delta = transferPriceDelta(opts.sourceUnitPrice, opts.targetUnitPrice, quantity);
  const sym = opts.currencySymbol || '';

  if (delta === null) {
    return {
      kind: 'unknown',
      headline: null,
      amountLine: null,
    };
  }

  if (delta === 0) {
    return {
      kind: 'none',
      headline: 'No adjustment required',
      amountLine: null,
    };
  }

  if (delta > 0) {
    return {
      kind: 'top_up',
      headline: 'Customer payment required',
      amountLine: formatMoneyMajor(sym, delta),
    };
  }

  return {
    kind: 'refund',
    headline: 'Partial refund due',
    amountLine: formatMoneyMajor(sym, delta),
  };
}
